/**
 * Temporary peer-delete investigation logs — ON by default until root cause is found.
 * Optional quiet: localStorage.setItem('swivu:ws-event-trace', '0'); location.reload()
 * Re-enable: localStorage.removeItem('swivu:ws-event-trace')
 *
 * Fail-safe: every entry point swallows errors so tracing never blocks apply/remove/sync.
 */
const FLAG_KEY = "swivu:ws-event-trace";
const PREFIX = "[ws-event-trace]";
/** How long a WS-applied create is treated as "recent" for peer-delete correlation (Oct 6 was ~4–6s). */
const RECENT_WS_ADD_TTL_MS = 60_000;

let uninstallPrevious = null;
/** @type {Map<string|number, number>} id -> addedAt ms */
const recentWsAddedEventIds = new Map();
/** @type {Map<string|number, number>} id -> addedAt ms */
const recentWsAddedAssignmentIds = new Map();

export function isWsEventTraceEnabled() {
  try {
    // Default ON so LogRocket / prod captures the rare peer-delete without someone enabling first.
    // Explicit '0' turns it off for local quiet runs.
    if (typeof window === "undefined" || !window.localStorage) return true;
    return window.localStorage.getItem(FLAG_KEY) !== "0";
  } catch {
    return true;
  }
}

export function wsEventTrace(label, data) {
  try {
    if (!isWsEventTraceEnabled()) return;
    if (data === undefined) {
      console.warn(PREFIX, label);
      return;
    }
    console.warn(PREFIX, label, data);
  } catch {
    // never throw from logging
  }
}

function pruneRecentMap(map, now = Date.now()) {
  for (const [id, at] of map) {
    if (now - at > RECENT_WS_ADD_TTL_MS) map.delete(id);
  }
}

function markRecentWsAdds(projectChanges) {
  const now = Date.now();
  pruneRecentMap(recentWsAddedEventIds, now);
  pruneRecentMap(recentWsAddedAssignmentIds, now);
  (projectChanges?.events?.added || []).forEach((row) => {
    if (row?.id != null) recentWsAddedEventIds.set(row.id, now);
  });
  (projectChanges?.assignments?.added || []).forEach((row) => {
    if (row?.id != null) recentWsAddedAssignmentIds.set(row.id, now);
  });
}

function recentMetaForEventIds(ids) {
  const now = Date.now();
  pruneRecentMap(recentWsAddedEventIds, now);
  return (ids || []).map((id) => {
    const at = recentWsAddedEventIds.get(id);
    return at == null
      ? { id, wasRecentlyWsAdded: false }
      : { id, wasRecentlyWsAdded: true, msSinceWsAdd: now - at };
  });
}

function recentMetaForAssignmentIds(ids) {
  const now = Date.now();
  pruneRecentMap(recentWsAddedAssignmentIds, now);
  return (ids || []).map((id) => {
    const at = recentWsAddedAssignmentIds.get(id);
    return at == null
      ? { id, wasRecentlyWsAdded: false }
      : { id, wasRecentlyWsAdded: true, msSinceWsAdd: now - at };
  });
}

function toMs(value) {
  if (value == null) return null;
  if (value instanceof Date) return value.getTime();
  const ms = new Date(value).getTime();
  return Number.isNaN(ms) ? null : ms;
}

function rangesOverlap(aStart, aEnd, bStart, bEnd) {
  return aStart < bEnd && aEnd > bStart;
}

function summarizeEvent(ev) {
  if (!ev) return null;
  return {
    id: ev.id,
    taskId: ev.taskId ?? ev.task,
    start: ev.startDate ?? ev.start_time,
    end: ev.endDate ?? ev.end_time,
    name: ev.name,
  };
}

function summarizeStoreChanges(changes) {
  if (!changes || typeof changes !== "object") return changes;
  const out = {};
  for (const op of ["added", "updated", "removed", "rows"]) {
    const rows = changes[op];
    if (!rows?.length) continue;
    out[op] = rows.map((r) => {
      if (r == null || typeof r !== "object") return r;
      return {
        id: r.id ?? r.$PhantomId,
        task: r.task ?? r.taskId,
        resource: r.resource ?? r.resourceId,
        event: r.event ?? r.eventId,
        start_time: r.start_time ?? r.startDate,
        end_time: r.end_time ?? r.endDate,
      };
    });
  }
  return out;
}

/** Safe summary for raw WS store payloads (SYNC/REVISION). */
export function summarizeWsStorePayload(storesSlice) {
  try {
    if (!storesSlice) return storesSlice;
    return summarizeStoreChanges(storesSlice);
  } catch {
    return undefined;
  }
}

/**
 * Before applying a remote event: does the peer already have another event on the
 * same resource overlapping the same dates? Read-only; does not mutate stores.
 */
export function findSameResourceDateOverlaps(project, { eventRow, assignmentRow }) {
  const resourceId = assignmentRow?.resource ?? assignmentRow?.resourceId;
  const startMs = toMs(eventRow?.start_time ?? eventRow?.startDate);
  const endMs = toMs(eventRow?.end_time ?? eventRow?.endDate);
  if (resourceId == null || startMs == null || endMs == null) {
    return { resourceId, startMs, endMs, overlaps: [], sameTaskOverlaps: [] };
  }

  const resource = project.resourceStore?.getById?.(resourceId);
  const events = resource?.events || [];
  const incomingId = eventRow.id;
  const incomingTaskId = eventRow.task ?? eventRow.taskId;

  const overlaps = [];
  const sameTaskOverlaps = [];

  events.forEach((ev) => {
    if (!ev || ev.id === incomingId || ev.isRemoved) return;
    const evStart = toMs(ev.startDate);
    const evEnd = toMs(ev.endDate);
    if (evStart == null || evEnd == null) return;
    if (!rangesOverlap(startMs, endMs, evStart, evEnd)) return;

    const summary = summarizeEvent(ev);
    overlaps.push(summary);
    if (incomingTaskId != null && ev.taskId === incomingTaskId) {
      sameTaskOverlaps.push(summary);
    }
  });

  return {
    incoming: {
      id: incomingId,
      taskId: incomingTaskId,
      resourceId,
      start: eventRow.start_time ?? eventRow.startDate,
      end: eventRow.end_time ?? eventRow.endDate,
    },
    alreadyExistsById: Boolean(project.eventStore?.getById?.(incomingId)),
    overlapCount: overlaps.length,
    sameTaskOverlapCount: sameTaskOverlaps.length,
    overlaps,
    sameTaskOverlaps,
  };
}

export function traceIncomingEventOverlaps(project, stores) {
  try {
    if (!isWsEventTraceEnabled()) return;

    const eventRows = stores?.events?.rows || [];
    const assignmentRows = stores?.assignments?.rows || [];
    if (!eventRows.length) return;

    const assignmentsByEventId = new Map();
    assignmentRows.forEach((row) => {
      const eventId = row.event ?? row.eventId;
      if (eventId == null) return;
      if (!assignmentsByEventId.has(eventId)) assignmentsByEventId.set(eventId, []);
      assignmentsByEventId.get(eventId).push(row);
    });

    eventRows.forEach((eventRow) => {
      const linked = assignmentsByEventId.get(eventRow.id) || [];
      if (!linked.length) {
        wsEventTrace("pre-apply event has no assignment row in same WS payload", {
          eventId: eventRow.id,
          taskId: eventRow.task,
        });
        return;
      }
      linked.forEach((assignmentRow) => {
        const result = findSameResourceDateOverlaps(project, { eventRow, assignmentRow });
        wsEventTrace(
          result.sameTaskOverlapCount
            ? "pre-apply SAME-TASK overlap on resource+dates (possible duplicate-path)"
            : result.overlapCount
              ? "pre-apply other-task overlap on resource+dates"
              : "pre-apply no overlap on resource+dates",
          result,
        );
      });
    });
  } catch {
    // never block WS apply
  }
}

export function traceBuiltProjectChanges(projectChanges) {
  try {
    if (!isWsEventTraceEnabled()) return;
    wsEventTrace("built projectChanges (events/assignments only)", {
      events: summarizeStoreChanges(projectChanges?.events),
      assignments: summarizeStoreChanges(projectChanges?.assignments),
    });
  } catch {
    // never block WS apply
  }
}

export function tracePostApplyEventPresence(project, projectChanges) {
  try {
    if (!isWsEventTraceEnabled()) return;
    const addedRows = projectChanges?.events?.added;
    if (!addedRows?.length) return;

    markRecentWsAdds(projectChanges);

    const postApply = addedRows.map((row) => {
      const ev = project.eventStore?.getById?.(row.id);
      return {
        id: row.id,
        stillInStore: Boolean(ev),
        isRemoved: Boolean(ev?.isRemoved),
        assignmentCount: ev?.assignments?.length ?? 0,
      };
    });
    wsEventTrace("post-apply event presence", postApply);
  } catch {
    // never block WS apply
  }
}

function projectFlags(project) {
  try {
    return {
      eventStoreIsRemoving: Boolean(project.eventStore?.isRemoving),
      eventStoreIsSettingData: Boolean(project.eventStore?.isSettingData),
      assignmentSyncingOnLoad: Boolean(project.assignmentStore?.isSyncingDataOnLoad),
      stmRestoring: Boolean(project.stm?.isRestoring),
      crudQueueBusy: Boolean(
        project.crudQueue && project.crudQueue.queuedCount > project.crudQueue.finishedCount,
      ),
    };
  } catch {
    return {};
  }
}

/**
 * Install remove/sync listeners. Replaces any previous install. Read-only listeners;
 * never return false / never mutate pack.
 * @returns {() => void} destroy
 */
export function installWsEventDisappearTrace(project) {
  try {
    if (typeof uninstallPrevious === "function") {
      uninstallPrevious();
      uninstallPrevious = null;
    }

    if (!project) {
      return () => {};
    }

    if (!isWsEventTraceEnabled()) {
      return () => {};
    }

    if (project._swivuWsEventTraceInstalled) {
      return () => {};
    }
    project._swivuWsEventTraceInstalled = true;

    wsEventTrace("installed on project");

    const eventBeforeRemove = project.eventStore.on("beforeRemove", ({ records }) => {
      try {
        const ids = (records || []).map((r) => r.id);
        const recent = recentMetaForEventIds(ids);
        const touchesRecentWsAdd = recent.some((r) => r.wasRecentlyWsAdded);
        // Full stack only for the Oct 6 signature (delete soon after remote create).
        wsEventTrace(
          touchesRecentWsAdd
            ? "eventStore beforeRemove (RECENT WS-ADDED — peer-delete suspect)"
            : "eventStore beforeRemove",
          {
            ids,
            recent,
            records: (records || []).map(summarizeEvent),
            flags: projectFlags(project),
            ...(touchesRecentWsAdd ? { stack: new Error("eventStore beforeRemove").stack } : {}),
          },
        );
      } catch {
        // never cancel remove
      }
    });

    const assignmentBeforeRemove = project.assignmentStore.on("beforeRemove", ({ records }) => {
      try {
        const rows = (records || []).map((r) => ({
          id: r.id,
          eventId: r.eventId ?? r.event?.id,
          resourceId: r.resourceId ?? r.resource?.id,
        }));
        const recent = recentMetaForAssignmentIds(rows.map((r) => r.id));
        const eventRecent = recentMetaForEventIds(rows.map((r) => r.eventId).filter((id) => id != null));
        const touchesRecentWsAdd =
          recent.some((r) => r.wasRecentlyWsAdded) || eventRecent.some((r) => r.wasRecentlyWsAdded);
        wsEventTrace(
          touchesRecentWsAdd
            ? "assignmentStore beforeRemove (RECENT WS-ADDED — peer-delete suspect)"
            : "assignmentStore beforeRemove",
          {
            rows,
            recent,
            eventRecent,
            flags: projectFlags(project),
            ...(touchesRecentWsAdd ? { stack: new Error("assignmentStore beforeRemove").stack } : {}),
          },
        );
      } catch {
        // never cancel remove
      }
    });

    const beforeSync = project.on("beforeSync", (event) => {
      try {
        const pack = event?.pack;
        const eventsRemoved = pack?.events?.removed;
        const assignmentsRemoved = pack?.assignments?.removed;
        const eventsAdded = pack?.events?.added;
        const assignmentsAdded = pack?.assignments?.added;
        if (!eventsRemoved?.length && !assignmentsRemoved?.length && !eventsAdded?.length) {
          return;
        }
        const removedIds =
          eventsRemoved?.map?.((r) => (typeof r === "object" ? r.id : r)) ?? eventsRemoved;
        const recentRemoved = recentMetaForEventIds(removedIds || []);
        const touchesRecentWsAdd = recentRemoved.some((r) => r.wasRecentlyWsAdded);
        wsEventTrace(
          touchesRecentWsAdd
            ? "beforeSync pack (RECENT WS-ADDED remove — peer-delete suspect)"
            : "beforeSync pack",
          {
            eventsAdded: eventsAdded?.map?.((r) => r.id ?? r.$PhantomId) ?? eventsAdded,
            eventsRemoved: removedIds,
            recentRemoved,
            assignmentsAdded: assignmentsAdded?.map?.((r) => r.id ?? r.$PhantomId) ?? assignmentsAdded,
            assignmentsRemoved:
              assignmentsRemoved?.map?.((r) => (typeof r === "object" ? r.id : r)) ?? assignmentsRemoved,
            hasBoardviews: Boolean(pack?.boardviews),
            flags: projectFlags(project),
          },
        );
      } catch {
        // never cancel sync — do not return false
      }
    });

    const destroy = () => {
      try {
        eventBeforeRemove?.();
        assignmentBeforeRemove?.();
        beforeSync?.();
      } catch {
        // ignore
      }
      try {
        project._swivuWsEventTraceInstalled = false;
      } catch {
        // ignore
      }
    };

    uninstallPrevious = destroy;
    return destroy;
  } catch {
    return () => {};
  }
}
